/**
 * Built-in tempo LFOs: divisions of master BPM (internal clock or derived tempo).
 * Values are continuous 0–1 (cosine-shaped), peaking on integer beats. Not part of performance.rig JSON.
 *
 * periodBeats = length in quarter-note beats for one full cycle of the LFO.
 */

/** @type {{ id: string, periodBeats: number }[]} */
export const BPM_BUILTIN_SPECS = [
  { id: 'bpm_bar', periodBeats: 4 },
  { id: 'bpm_whole', periodBeats: 4 },
  { id: 'bpm_half', periodBeats: 2 },
  { id: 'bpm_beat', periodBeats: 1 },
  { id: 'bpm_quarter_straight', periodBeats: 1 },
  { id: 'bpm_eighth_straight', periodBeats: 0.5 },
  { id: 'bpm_sixteenth_straight', periodBeats: 0.25 },
  { id: 'bpm_thirty_second_straight', periodBeats: 0.125 },
  { id: 'bpm_sixty_fourth_straight', periodBeats: 0.0625 },
  { id: 'bpm_quarter_triplet', periodBeats: 2 / 3 },
  { id: 'bpm_eighth_triplet', periodBeats: 1 / 3 },
  { id: 'bpm_sixteenth_triplet', periodBeats: 1 / 6 },
];

/** Ordered ids for UI (Performance → binding dropdowns). */
export const BPM_BUILTIN_SIGNAL_IDS = BPM_BUILTIN_SPECS.map((s) => s.id);

/** @type {Map<string, number>} */
const ORDER_INDEX = new Map(BPM_BUILTIN_SIGNAL_IDS.map((id, i) => [id, i]));

/**
 * @param {number} totalBeats Continuous beat counter (e.g. time * bpm / 60).
 * @returns {Record<string, number>}
 */
export function computeBpmBuiltinSignals(totalBeats) {
  const b = Number(totalBeats) || 0;
  /** @type {Record<string, number>} */
  const out = {};
  for (const { id, periodBeats } of BPM_BUILTIN_SPECS) {
    const p = Math.max(1e-9, periodBeats);
    out[id] = 0.5 + 0.5 * Math.cos((2 * Math.PI * b) / p);
  }
  return out;
}

/**
 * Sort for binding dropdowns: built-in bpm divisions first (fixed order), then other bpm_*, then rest A–Z.
 * @param {string[]} names
 */
export function sortSignalNamesForUi(names) {
  const arr = [...new Set(names.map((n) => String(n).trim()).filter(Boolean))];
  arr.sort((a, b) => {
    const ia = ORDER_INDEX.has(a) ? ORDER_INDEX.get(a) : -1;
    const ib = ORDER_INDEX.has(b) ? ORDER_INDEX.get(b) : -1;
    if (ia >= 0 && ib >= 0) return ia - ib;
    if (ia >= 0) return -1;
    if (ib >= 0) return 1;
    const aBpm = a.startsWith('bpm_');
    const bBpm = b.startsWith('bpm_');
    if (aBpm && !bBpm) return -1;
    if (!aBpm && bBpm) return 1;
    return a.localeCompare(b);
  });
  return arr;
}
